import { useDndContext, useDndMonitor, useDroppable } from '@dnd-kit/core';
import { Cloud, FolderRoot, Plus } from 'lucide-react';
import {
  contextMenuOpenStyles,
  dropStyle,
  hoverStyles,
  selectedStyles,
  selectedTreeItemIconStyles,
  styles,
} from '@/components/sidebar/sidebar';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { MenuProvider, contextPrimitives } from '@/components/menu-renderer';
import { NewFolderMenuItem } from '@/components/menu-items/node-menu-items';
import { MoveNodeCommand } from '@/hooks/commands/folder/MoveNodeCommand';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useMoveNodesMutation } from '@/hooks/tree/useMoveNodesMutation';
import { useCommandContext } from '@/context/CommandContext';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useToast } from '@/components/ui/use-toast';
import { DraggableItems } from '@/types/dragDrop';
import { Button } from '@/components/ui/button';
import { EntityType } from '@/types/entity';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export function TreeHome({ disabled }: { disabled: boolean }) {
  const { navigateAndReset } = useDamNavigation();
  const { isHomePage, onTrashSearch } = useCurrentPage();
  const { toast } = useToast();
  const { moveNodes } = useMoveNodesMutation();
  const { apply } = useCommandContext();
  const { active: draggedItem } = useDndContext();
  const tToasts = useTranslations('toasts');
  const tCommon = useTranslations('common');
  const tTree = useTranslations('tree');
  const openModal = useOpenModal();

  const { setNodeRef, isOver } = useDroppable({
    id: 'home-folder',
    data: {
      accepts: [DraggableItems.FOLDER],
    },
    disabled,
  });

  const showOverStyles = isOver && draggedItem?.data?.current?.type === DraggableItems.FOLDER;
  const isHomeSelected = !disabled && isHomePage && !onTrashSearch;

  useDndMonitor({
    onDragEnd(event) {
      void (async () => {
        const { active, over } = event;
        if (active.data?.current && active.data.current?.type === DraggableItems.FOLDER && over?.id === 'home-folder') {
          const moveNodeCommand = MoveNodeCommand(
            moveNodes,
            active.data.current.parentId,
            [active.data.current.id],
            active.data?.current?.type,
          );
          await apply(moveNodeCommand);
          toast({
            title: tToasts('folder.moved.title'),
            description: tToasts('folder.moved.description'),
          });
        }
      })();
    },
  });

  return (
    <ContextMenu>
      <div
        className="flex flex-1 flex-col gap-y-1.5 focus-within:transition-colors focus-within:duration-150"
        title={tCommon('home')}
      >
        <ContextMenuTrigger disabled={disabled} asChild>
          <div
            ref={setNodeRef}
            className={cn('group/tree-item relative flex w-full items-center px-2', styles, {
              [hoverStyles]: !showOverStyles,
              [contextMenuOpenStyles]: !isHomeSelected,
              [selectedStyles]: isHomeSelected,
            })}
          >
            <div className={cn('flex w-full items-center rounded-md py-1', { [dropStyle]: showOverStyles })}>
              <button
                disabled={disabled}
                className={cn(
                  'flex min-w-0 flex-1 items-center rounded-sm outline-hidden focus-within:outline-none focus-visible:ring-2',
                  isHomeSelected ? 'focus-visible:ring-text-inverted' : 'focus-visible:ring-highlight-hover-color',
                )}
                onClick={() => {
                  void navigateAndReset('/browse', {}, EntityType.Asset);
                }}
              >
                <Cloud
                  className={cn(
                    'duration-250 ml-1 icon-6 shrink-0',
                    !disabled && 'group-hover/tree-item:stroke-text-text-gray',
                    isHomeSelected ? selectedTreeItemIconStyles : 'stroke-text-text-gray',
                  )}
                  strokeWidth={2}
                />
                <div className="ml-2 flex w-full min-w-0 justify-between">
                  <span className="line-clamp-1 text-left text-base break-all">{tCommon('home')}</span>
                </div>
              </button>
              {/* The Home row's context menu holds exactly one item, "New Folder". This button is
                  that item promoted to a visible control, so root folders can be created without
                  discovering the right-click menu. It is always visible rather than hover-revealed
                  because the row has no long-press menu and a hover-only control is unreachable on
                  touch. `mr-1` puts its 24px glyph centre 26px from the row's right edge, the same
                  column as the folder rows' `…` triggers (`folder.tsx` `absolute right-4` + icon-5). */}
              {!disabled && (
                <Button
                  variant="main-toolbar"
                  size="main-toolbar"
                  icon={Plus}
                  className={cn(
                    'mr-1 shrink-0 focus-visible:ring-2',
                    isHomeSelected
                      ? 'focus-visible:ring-text-inverted [&>svg]:stroke-text-inverted hover:[&>svg]:stroke-text-inverted'
                      : 'focus-visible:ring-highlight-hover-color',
                  )}
                  tooltip={tTree('newRootFolder')}
                  data-cy="tree-home-new-folder-button"
                  onClick={() => {
                    openModal(ModalId.CreateFolder);
                  }}
                />
              )}
            </div>
          </div>
        </ContextMenuTrigger>
      </div>
      <ContextMenuContent>
        <MenuProvider primitives={contextPrimitives}>
          <NewFolderMenuItem icon={FolderRoot} />
        </MenuProvider>
      </ContextMenuContent>
    </ContextMenu>
  );
}
